import { gql } from '@apollo/client'
import { useLazyQuery } from '@apollo/client/react'
import { useTranslation } from 'next-i18next'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import Carousel from 'src/components/Carousel/Carousel'
import LocationSearchCard, {
  CARD_CAROUSEL_ARROW_TOP,
  type RecommendedCarouselSurface
} from 'src/components/LocationSearchCard'
import { DEFAULT_CENTER, DEFAULT_ZOOM } from 'src/constants'
import {
  PILLAR_PAGE_MAP,
  PillarPage,
  SEARCH_PAGE_CITY_MAP,
  SearchPageCity
} from 'src/constants/searchPages'
import useLogger from 'src/context/LoggerProvider/useLogger'
import useHasScrolled from 'src/hooks/useHasScrolled'
import usePillarNames from 'src/hooks/usePillarNames'
import useUserLocation from 'src/hooks/useUserLocation'
import ga from 'src/utils/pixels/gtag'
import LoadingRecommendedCard from '../../components/Loaders/LoadingRecommendedCard'
import { CARD_LOCATION_FIELDS } from '../../constants/fragments'
import {
  ICoordinates,
  ILocation,
  IQuery,
  IQueryRecommendedLocationsArgs
} from '../../types/graphql'
import { RedirectUrls } from '../../types/types'
import CityLinks from './CityLinks'

const RECOMMENDED_LOCATIONS = gql`
  ${CARD_LOCATION_FIELDS}
  query RecommendedLocations(
    $pagination: PaginationInput!
    $searchInput: SearchLocationsInput!
  ) {
    recommendedLocations(pagination: $pagination, searchInput: $searchInput) {
      total
      hits {
        ...CardLocationFields
      }
      fallback
    }
  }
`

/**
 * How long an eager rail waits for the IP-geo center before it queries around
 * `DEFAULT_CENTER` anyway. `ipInfo` arrives with AuthProvider's first query
 * (client-side, a few hundred ms on 4G); non-MX visitors never get one, so
 * the rail must not wait forever. Waiting at all matters: firing on the very
 * first render would query the default city for every visitor and then
 * re-query the moment geo resolved — two searches per pageview.
 */
const EAGER_GEO_TIMEOUT_MS = 1500

/** "Ver más →" — editorial text link: ink words (AA on white), the brand
 *  blue lives in the underline, ≥44px tall for a thumb. Display is set at
 *  the call site so `hidden` and `inline-flex` never share a class list. */
const VIEW_MORE_LINK_CLASS =
  'min-h-11 shrink-0 items-center text-sm font-medium text-ink-700 underline decoration-blue-500/40 underline-offset-[6px] transition-colors duration-200 hover:text-blue-500 hover:decoration-blue-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink-700'

type IProps = {
  /** Mount point — REQUIRED so card clicks and "Ver más" are attributable
   *  to the carousel that produced them (home vs pillar vs blog vs …). */
  surface: RecommendedCarouselSurface
  pillar?: PillarPage
  /**
   * Renders a segmented control that switches the rail between these pillars;
   * the first entry is the default. The home page passes storage then parking
   * so the rail matches the hero (storage-first, parking one tap away) instead
   * of mixing every space type in query order. Pillar pages pass `pillar`.
   */
  pillarToggle?: PillarPage[]
  ctaHref?: string
  defaultLocations?: ILocation[]
  effectiveCity?: SearchPageCity | string
  /**
   * Query as soon as a center is known (or `EAGER_GEO_TIMEOUT_MS` elapses)
   * instead of waiting for the first scroll event. Use where the rail sits
   * directly under the hero. The scroll gate stays as a fallback path, so a
   * visitor who scrolls before geo resolves is served either way.
   *
   * History: wired 2026-04-26, silently dropped by the 2026-05-05 rewrite,
   * re-wired 2026-09-06 — the home rail had been painting skeletons until the
   * first scroll for four months while its call site said "eager".
   */
  eagerLoad?: boolean
  className?: string
}

const PillarToggleButton = ({
  pillar,
  active,
  onSelect
}: {
  pillar: PillarPage
  active: boolean
  onSelect: (pillar: PillarPage) => void
}) => {
  const { label } = usePillarNames(pillar)
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={() => onSelect(pillar)}
      className={`min-h-11 flex-1 rounded-full px-4 text-[13px] font-medium whitespace-nowrap transition-colors duration-200 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink-700 sl:flex-none ${
        active ? 'bg-ink-700 text-white-500' : 'text-ink-700 hover:bg-cream-300'
      }`}
    >
      {label}
    </button>
  )
}

const SpotsSection = ({
  surface,
  pillar,
  pillarToggle,
  ctaHref,
  eagerLoad
}: IProps) => {
  const [activePillar, setActivePillar] = useState<PillarPage | undefined>(
    pillar ?? pillarToggle?.[0]
  )
  const [geoTimedOut, setGeoTimedOut] = useState(false)
  const logger = useLogger()
  const { center: effectiveCenter, loading } = useUserLocation()
  const hasScrolled = useHasScrolled()
  const { t } = useTranslation('landing')
  // Locale-aware — PILLAR_PAGE_MAP.label is Spanish-only, which rendered
  // "Mini Bodegas near to you" (ES noun + EN tail) on /en.
  const { label: pillarLabel } = usePillarNames(
    activePillar ?? PillarPage.SelfStorage
  )

  const [recommendedLocations, { data, error: queryError, loading: fetching }] =
    useLazyQuery<
      Pick<IQuery, 'recommendedLocations'>,
      IQueryRecommendedLocationsArgs
    >(RECOMMENDED_LOCATIONS)

  useEffect(() => {
    if (queryError) logger.error('Error retrieving locations.', queryError)
  }, [queryError, logger])

  // Derived, not state: the old `useState(DEFAULT_CENTER)` + sync effect
  // committed one render behind `effectiveCenter`, so the query effect fired
  // once with the default center and again with the real one.
  const center: ICoordinates = effectiveCenter ?? DEFAULT_CENTER

  const closestCity = useMemo(() => {
    // Fallback to calculating closest city by coordinates
    const [closest] = Object.entries(SEARCH_PAGE_CITY_MAP)
      .filter(([, { center: cityCenter }]) => cityCenter)
      .map(([key, { center: cityCenter }]): [SearchPageCity, ICoordinates] => [
        key as SearchPageCity,
        cityCenter as ICoordinates
      ])
      .map(([key, { lat, lng }]): [SearchPageCity, number] => [
        key,
        Math.sqrt((lat - center.lat) ** 2 + (lng - center.lng) ** 2)
      ])
      .sort((a, b) => a[1] - b[1])
    return closest[0]
  }, [center])

  useEffect(() => {
    if (!eagerLoad) return
    const handle = setTimeout(() => setGeoTimedOut(true), EAGER_GEO_TIMEOUT_MS)
    return () => clearTimeout(handle)
  }, [eagerLoad])

  const eagerReady =
    Boolean(eagerLoad) && (Boolean(effectiveCenter) || geoTimedOut)
  const scrollReady = !loading && hasScrolled
  const ready = eagerReady || scrollReady

  useEffect(() => {
    if (!ready) return
    const pillarQuery = activePillar
      ? PILLAR_PAGE_MAP[activePillar]?.query
      : undefined

    recommendedLocations({
      variables: {
        pagination: { pageSize: 15, from: 0 },
        searchInput: { center, zoom: DEFAULT_ZOOM, ...pillarQuery }
      }
    })
  }, [ready, center, recommendedLocations, activePillar])

  const handlePillarSelect = useCallback(
    (next: PillarPage) => {
      if (next === activePillar) return
      setActivePillar(next)
      ga.event('recommended_pillar_toggle', { surface, pillar: next })
    },
    [activePillar, surface]
  )

  const locations = data?.recommendedLocations?.hits
  const cityKnown = Boolean(effectiveCenter) && Boolean(closestCity)
  const headingNoun = activePillar ? pillarLabel : 'Spots'

  // "Ver más" follows the rail: a toggled rail sends the visitor to the
  // active pillar's city page (where the hero sends them too); every other
  // surface keeps the map search it always had.
  const pillarCityHref = activePillar
    ? cityKnown
      ? `/${activePillar}/${closestCity}`
      : `/${activePillar}`
    : RedirectUrls.Search
  const viewMoreHref =
    ctaHref ?? (pillarToggle ? pillarCityHref : RedirectUrls.Search)
  const trackViewMore = () =>
    ga.event('recommended_view_more_click', { surface })

  const hasToggle = Boolean(pillarToggle && pillarToggle.length > 1)

  return (
    <section className="w-full bg-white-500 pt-12 pb-6 md:pt-16 md:pb-10">
      <div className="mx-auto max-w-screen-xxl">
        {/* Header — heading, then the ONE control that changes the rail
            (the pillar toggle) and "Ver más". Cities live under the
            carousel: they navigate away, they don't filter, so they must
            not sit beside a toggle that does. */}
        <div className="mb-8 flex flex-col gap-5 px-5 md:flex-row md:items-end md:justify-between md:gap-8 lg:px-7 xl:px-12">
          <h2 className="font-display text-[26px] leading-[1.15] font-medium text-balance text-ink-700 md:text-[34px]">
            {cityKnown ? (
              <>
                {`${headingNoun} ${t('INDEX_RECOMMENDED.IN_CITY')} `}
                <span className="text-blue-500 italic">
                  {SEARCH_PAGE_CITY_MAP[closestCity].cityName}
                </span>
              </>
            ) : (
              `${headingNoun} ${t('INDEX_RECOMMENDED.HEADING')}`
            )}
          </h2>
          <div className="flex items-center gap-6 md:shrink-0">
            {hasToggle && pillarToggle && (
              <div
                role="group"
                aria-label={t('INDEX_RECOMMENDED.PILLAR_TOGGLE_LABEL')}
                className="flex w-full items-center gap-0.5 rounded-full border border-ink-100/20 p-0.5 sl:w-fit"
              >
                {pillarToggle.map((option) => (
                  <PillarToggleButton
                    key={option}
                    pillar={option}
                    active={option === activePillar}
                    onSelect={handlePillarSelect}
                  />
                ))}
              </div>
            )}
            <a
              href={viewMoreHref}
              onClick={trackViewMore}
              className={`hidden md:inline-flex ${VIEW_MORE_LINK_CLASS}`}
            >
              {`${t('INDEX_RECOMMENDED.BUTTON_SEARCH')} →`}
            </a>
          </div>
        </div>

        {/* Carousel. Dims (opacity only — compositor-friendly) while a
            pillar switch is in flight so the old cards read as stale
            rather than vanishing into a skeleton for 300 ms. */}
        <div
          className={`relative w-full transition-opacity duration-200 ${
            fetching && locations ? 'opacity-60' : ''
          }`}
        >
          {!locations ? (
            <LoadingRecommendedCard />
          ) : locations.length === 0 ? null : (
            <Carousel
              carouselOptions={{
                loop: true,
                containScroll: false,
                dragFree: true
              }}
              carouselOptionsDesktop={{ align: 'start' }}
              items={locations}
              showButtons
              marginButtons={4}
              arrowTopClassName={CARD_CAROUSEL_ARROW_TOP}
            >
              {({ item }) => (
                <div key={item.id} className="pb-2">
                  <LocationSearchCard
                    location={item as ILocation}
                    surface={surface}
                    styleDefault={false}
                  />
                </div>
              )}
            </Carousel>
          )}
        </div>

        {/* Browse by city — a hairline-ruled footer, not a second filter.
            Mobile folds "Ver más" into the label row so the section ends
            in one line instead of a centered link plus three chip rows. */}
        <nav
          aria-label={t('INDEX_RECOMMENDED.BY_CITY', { noun: headingNoun })}
          className="mx-5 mt-6 flex flex-col gap-1 border-t border-ink-100/15 pt-4 md:mt-8 md:flex-row md:items-center md:gap-6 lg:mx-7 xl:mx-12"
        >
          <div className="flex items-center justify-between md:shrink-0">
            <span className="text-xs font-medium tracking-[0.08em] text-ink-200 uppercase">
              {t('INDEX_RECOMMENDED.BY_CITY', { noun: headingNoun })}
            </span>
            <a
              href={viewMoreHref}
              onClick={trackViewMore}
              className={`inline-flex md:hidden ${VIEW_MORE_LINK_CLASS}`}
            >
              {`${t('INDEX_RECOMMENDED.BUTTON_SEARCH')} →`}
            </a>
          </div>
          <CityLinks
            closestCity={cityKnown ? closestCity : undefined}
            pillar={activePillar}
          />
        </nav>
      </div>
    </section>
  )
}

export default React.memo(SpotsSection)
